import { ChevronRightIcon, UserIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/20/solid';

import { ShiftCard as ShiftCardData } from '../api/types';
import { cardIdentity, formatTimeRange } from './ShiftCard';

// One shift as a full-width row in the Day view (GRAVY-10329).
//
// Figma `Admin-Dashboard x 1024` → `Item 1` / `Item 3` (39124:32648 / 32833):
// a bordered card, 16px padding, 24px between its header and the fill strip.
// The header is client → skill → time → location with a status badge and a
// chevron pinned right; the text column is `flex: 1` with `min-width: 0` so a
// long client name ellipsises instead of pushing the chevron off.
//
// ## The strip is conditional, and that IS the visual priority system
//
// The frame draws the fill indicator two ways. A shift that needs attention gets
// a bordered, tinted strip (red border, 12% red fill); a fully staffed one gets
// the bare fraction in green with no box at all. So the border/background is the
// signal — an admin scanning the list sees urgency without reading a word, which
// is what the ticket asks for. Rendering the box unconditionally would make
// every row look equally urgent.
//
// The band comes from `fill_status`, the same value the week grid colours by and
// the same one the summary pills filter on, so the two views cannot disagree.

type Props = {
  shift: ShiftCardData;
  onOpen: (shiftId: number) => void;
};

/** Identity and the time range come from the WEEK card, deliberately: the same
    shift must read identically in both views (GRAVY-10290). */
const identity = cardIdentity;

/** "DC 20001" — the location identifier the design shows. */
const location = (s: ShiftCardData) =>
  [s.state, s.zip].filter(Boolean).join(' ') || s.location_code || '';

const DayShiftCard = ({ shift, onOpen }: Props) => {
  const band = shift.fill_status;
  const needsAttention = band === 'critical' || band === 'at_risk';
  const filled = (shift.hired_count ?? 0) >= (shift.quantity ?? 0);

  return (
    <div
      className={`dashv2-daycard is-${band ?? 'unknown'}`}
      id={`dashv2-daycard-${shift.id}`}
      data-testid="dayShiftCard"
      data-fill-status={band ?? ''}
    >
      <div className="dashv2-daycard-head">
        <div className="dashv2-daycard-main">
          <p className="dashv2-daycard-title" title={identity(shift)}>
            {/* A restricted-pool job is locked in the week grid too; the icon
                travels with the identity rather than being a separate column. */}
            {shift.restricted_pool && (
              <LockClosedIcon
                className="dashv2-daycard-lock"
                aria-label="Restricted pool"
              />
            )}
            {identity(shift)}
          </p>
          <p className="dashv2-daycard-role">{shift.skill_name}</p>
          <p className="dashv2-daycard-time">{formatTimeRange(shift)}</p>
          <p className="dashv2-daycard-loc">{location(shift)}</p>
        </div>

        {needsAttention && (
          <span className="dashv2-daycard-badge">Needs attention</span>
        )}

        <button
          type="button"
          className="dashv2-daycard-open"
          aria-label={`Open shift details for ${identity(shift)}`}
          onClick={() => onOpen(shift.id)}
        >
          <ChevronRightIcon />
        </button>
      </div>

      <div
        className={`dashv2-daycard-fill${needsAttention ? ' is-alert' : ''}`}
      >
        <span
          className={`dashv2-daycard-metric${filled ? ' is-filled' : ''}`}
          // Read as one fact by a screen reader; the icon alone says nothing.
          aria-label={`${shift.hired_count} of ${shift.quantity} hired`}
        >
          <UserIcon aria-hidden="true" />
          {/* Scales past 1 — the fraction is printed, never a boolean. */}
          {shift.hired_count}/{shift.quantity}
        </span>
      </div>
    </div>
  );
};

export default DayShiftCard;
